import React, { FC, useCallback, useMemo, useRef } from 'react'

import { ErrorMessage, Field, Form, Formik } from 'formik'
import { groupBy } from 'lodash'
import * as yup from 'yup'

import { DELETED_FOLDER, ROOT_FOLDERS_CHILDREN } from './FileManager'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import SelectField from '@/components/FormikInputs/SelectField'
import { ROOT_FOLDERS, setActiveModal } from '@/features/FileManager/fileManagerSlice'
import { KeyButton } from '@/key-components/KeyButton'
import { useUpdateUserDocumentMutation } from '@/services/documents'
import { UserDocument } from '@/types/unlock'
import { fileNameRegex } from '@/utils/documents'

interface FileEditModalProps {
  documents: any[]
  mode: string
}

export const FileEditModal: FC<FileEditModalProps> = (props) => {
  const dispatch = useAppDispatch()

  const file = useAppSelector((state) => state.fileManager.activeModal.file)

  const { mode, documents } = props

  const [updateDoc] = useUpdateUserDocumentMutation()
  const lastSaved = useRef(JSON.stringify(null))

  let initialValues = {}
  let formSchema = {}

  if (mode === 'move') {
    initialValues = { ...initialValues, folder: file.tag, newFolder: '' }
    formSchema = yup.object().shape({
      folder: yup.string().nullable().min(2),
      newFolder: yup
        .string()
        .required()
        .matches(fileNameRegex, 'Files can only be moved either to root folders or a subfolder'),
      validateFolder: yup.string().when(['folder', 'newFolder'], {
        is: (folder: string, newFolder: string) => !folder && !newFolder,
        then: yup.string().required('You must choose a folder'),
      }),
    })
  } else if (mode === 'rename') {
    initialValues = { ...initialValues, fileName: file.fileName?.split('.')[0] }
    formSchema = yup.object().shape({ fileName: yup.string().required().min(2) })
  }

  const submit = useCallback(
    async (values) => {
      if (JSON.stringify(values) === lastSaved.current) return false

      let payload: Partial<UserDocument> = { uuid: file.uuid }

      if (mode === 'move') {
        payload = { ...payload, tag: values.newFolder || values.folder }
      } else if (mode == 'rename') {
        payload = {
          ...payload,
          original_filename: values.fileName + '.' + file.fileName?.split('.')[1],
        }
      }

      // Send to API (async)
      const response = await updateDoc(payload)

      if ((response as any)?.data) {
        dispatch(setActiveModal(null))
      }

      // Store new ref
      lastSaved.current = JSON.stringify(values)

      return response
    },
    [file.fileName, file.uuid, mode, dispatch, updateDoc]
  )

  const folderOptions = useMemo(() => {
    let foldersOptionsBase: any = {
      root: ROOT_FOLDERS.map((rootFolder) => ({ value: rootFolder, label: rootFolder })),
    }

    ROOT_FOLDERS.forEach(
      (rootFolder) =>
        (foldersOptionsBase[rootFolder] = {
          label: rootFolder,
          options: [],
        })
    )

    foldersOptionsBase = Object.keys(groupBy(documents, 'tag'))
      .filter((folder) => !ROOT_FOLDERS.includes(folder) && folder !== DELETED_FOLDER)
      .reduce((acc, folder) => {
        const splitedFolder = folder.split('/')
        const [rootFolder, childFolder] = splitedFolder

        const folderName = childFolder ? childFolder : folder

        acc[rootFolder]?.options.push({ value: folder, label: folderName })

        return acc
      }, foldersOptionsBase)

    foldersOptionsBase = ROOT_FOLDERS.reduce((acc, rootFolder) => {
      const rootFolderChildren = ROOT_FOLDERS_CHILDREN[rootFolder].children

      const optionsToAppend = rootFolderChildren.map((childFolder) => ({
        value: `${rootFolder}/${childFolder}`,
        label: childFolder,
      }))

      acc[rootFolder].options = [...acc[rootFolder].options, ...optionsToAppend]

      return acc
    }, foldersOptionsBase)

    const foldersRoot = foldersOptionsBase.root
    delete foldersOptionsBase.root

    return foldersRoot.concat(Object.values(foldersOptionsBase))
  }, [documents])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={async (values) => {
        await submit(values)
      }}
    >
      {({ isSubmitting, isValid }) => (
        <Form
          spellCheck={false}
          autoComplete='off'
        >
          {mode === 'rename' && (
            <div className='mb-4'>
              <Field
                id='fileName'
                name='fileName'
                placeholder='File name (without extension)'
                disabled={isSubmitting}
                className='w-full p-2 border-gray-200 focus:border-sky-300 text-[12px] shadow'
              />

              <ErrorMessage name='fileName'>
                {(message: string) => <small className='text-error'>{message}</small>}
              </ErrorMessage>
            </div>
          )}
          {mode === 'move' && (
            <div className='mb-4'>
              <SelectField
                name='folder'
                id='folder'
                placeholder='Choose a folder'
                options={folderOptions}
                disabled={isSubmitting}
              />

              <div className='py-3 text-center'>OR</div>

              <Field
                id='newFolder'
                name='newFolder'
                placeholder='Create a new folder'
                disabled={isSubmitting}
                className='w-full p-2 border-gray-200 focus:border-sky-300 text-[12px] shadow'
              />
              <div className='text-xs mt-2'>
                Supports up to one subfolder e.g:{' '}
                <span className='text-yellow-800'>Deal Files/my sub-folder</span>
              </div>
              <ErrorMessage
                name='newFolder'
                render={() => (
                  <span className='text-xs text-red-500 mt-4'>
                    Please use existing parent folders without spaces or slashes at the end.
                  </span>
                )}
              />
            </div>
          )}
          <div className='flex items-center justify-center w-full mt-2'>
            <KeyButton
              size={'medium'}
              variant={'primary'}
              className='grow-0'
              type='submit'
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? 'Submitting' : 'Submit'}
            </KeyButton>
          </div>
        </Form>
      )}
    </Formik>
  )
}
